import { useGetStrings } from "@app/store/service/cmsService"
import { handleKeyboardSelect, handleKeyPress } from "@app/utils/accessibility"
import { KEYBOARD_KEY_SET } from "@app/utils/constants"
import { faX } from "@fortawesome/free-solid-svg-icons/faX"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useViewport } from "@tecreation/funnel-components"
import { useSelector } from "@tecreation/js-state-react"
import { AnimatePresence, domAnimation, LazyMotion, m } from "framer-motion"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import BaseStack from "./BaseStack"
import "./DateSelectionTicketstack.css"

const interactiveElements =
	'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export default function DateSelectionTicketstack() {
	const { sm } = useViewport()
	const [show, setShow] = useState(false)
	const [modalMounted, setModalMounted] = useState(false)
	const {
		data: { buttonClose },
	} = useGetStrings(undefined, { defaultData: {} })

	const selectedDay = useSelector(state => state.order.selectedDay)
	const modalRef = useRef(null)
	const previousFocusRef = useRef(null)
	const hasFocusedRef = useRef(false)

	const closeButtonRef = element => {
		if (element && !hasFocusedRef.current) {
			previousFocusRef.current = document.activeElement
			element.focus()
			hasFocusedRef.current = true
		}
	}

	useEffect(() => {
		setShow(true)
		hasFocusedRef.current = false
	}, [selectedDay])

	useEffect(() => {
		if (!show || !sm || !modalMounted) return

		const handleKeyDown = e => {
			handleKeyPress(e, {
				[KEYBOARD_KEY_SET.Escape]: () => {
					setShow(false)
				},
				[KEYBOARD_KEY_SET.Tab]: () => {
					if (!modalRef.current) return

					// Get focusable elements in modal
					const modalFocusable = Array.from(modalRef.current.querySelectorAll(interactiveElements))

					const footerButton = document.querySelector(".contentFooter button")

					if (modalFocusable.length === 0) return

					const last = modalFocusable[modalFocusable.length - 1]

					// When Tab on last element, go to footer button
					if (document.activeElement === last) {
						e.preventDefault()
						footerButton.focus()
					}
				},
				[KEYBOARD_KEY_SET.ShiftAndTab]: () => {
					// Get focusable elements in modal
					const modalFocusable = Array.from(modalRef.current.querySelectorAll(interactiveElements))

					const footerButton = document.querySelector(".contentFooter button")

					if (modalFocusable.length === 0) return

					const first = modalFocusable[0]
					const last = modalFocusable[modalFocusable.length - 1]

					/**
					 * If closeModal is present, it means we have the mobile view where focus
					 * should stay trapped inside the modal, not go back to focusing the calendar
					 */
					const isCloseModalButton = first.classList.contains("dateSelection__closeButton")

					// When Shift+Tab on first element, go back to calendar
					if (document.activeElement === first && !isCloseModalButton) {
						e.preventDefault()
						if (window.restoreDateSelectionCalendarFocus) {
							window.restoreDateSelectionCalendarFocus()
						}
						return
					}

					// When Shift+Tab on footer, jump back to ticket selection
					if (document.activeElement === footerButton) {
						e.preventDefault()
						last.focus()
					}
				},
			})
		}

		document.addEventListener("keydown", handleKeyDown)
		return () => document.removeEventListener("keydown", handleKeyDown)
	}, [show, sm, modalMounted])

	useEffect(() => {
		if (!show && previousFocusRef.current) {
			previousFocusRef.current.focus()
			previousFocusRef.current = null
		}
	}, [show])

	if (!selectedDay) {
		return null
	}

	if (sm) {
		return createPortal(
			<div
				className="dateSelection__ticketoverlay of-hidden"
				style={{ display: show ? "block" : "none" }}
				role="dialog"
				aria-modal="true"
				aria-labelledby="date-selection-ticket-title"
			>
				<LazyMotion features={domAnimation}>
					<AnimatePresence>
						{show && (
							<m.div
								ref={viewRef => {
									modalRef.current = viewRef
									modalMounted !== !!viewRef && setModalMounted(!!viewRef)
								}}
								key="lounger-selection-bottom"
								className="bottom__btn color-bg-default clickable fill absolute"
								style={{ bottom: "0", transition: ".3s", transformOrigin: "bottom center" }}
								initial={{
									scaleY: "0%",
								}}
								animate={{
									scaleY: "100%",
									transition: {
										ease: "linear",
										duration: 0.1,
									},
								}}
								exit={{
									height: 0,
									transition: { duration: 0.3 },
								}}
							>
								<button
									ref={closeButtonRef}
									onClick={() => setShow(false)}
									onKeyDown={e => {
										handleKeyboardSelect(e, () => {
											setShow(false)
											window.restoreDateSelectionCalendarFocus()
										})
									}}
									className="dateSelection__closeButton absolute right-0 p-3"
									aria-label={buttonClose}
								>
									<FontAwesomeIcon icon={faX} />
								</button>
								<BaseStack />
							</m.div>
						)}
					</AnimatePresence>
				</LazyMotion>
			</div>,
			document.body,
		)
	}

	return (
		<div
			data-ticketstack-container
			onKeyDown={e => {
				// When Shift+Tab is pressed on the first focusable element, go back to calendar
				handleKeyPress(e, {
					[KEYBOARD_KEY_SET.ShiftAndTab]: () => {
						const ticketstackContainer = e.currentTarget
						const focusableElements = ticketstackContainer.querySelectorAll(interactiveElements)

						if (focusableElements.length > 0 && document.activeElement === focusableElements[0]) {
							e.preventDefault()
							if (window.restoreDateSelectionCalendarFocus) {
								window.restoreDateSelectionCalendarFocus()
							}
						}
					},
				})
			}}
		>
			<BaseStack />
		</div>
	)
}
